import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchTree,
  fetchAvailableUsers,
  assignNode,
  removeNode,
  setSelectedNode,
  clearError,
} from "../../redux/slices/mlmTreeSlice";
import api from "../../api/axios";
import { Modal, Button, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import "./MlmTreePage.css";

const LEVEL_COLORS = [
  { bg: "#6366f1", light: "#eef2ff", text: "#4338ca" },
  { bg: "#8b5cf6", light: "#f5f3ff", text: "#6d28d9" },
  { bg: "#a855f7", light: "#faf5ff", text: "#7e22ce" },
  { bg: "#d946ef", light: "#fdf4ff", text: "#a21caf" },
  { bg: "#ec4899", light: "#fdf2f8", text: "#be185d" },
  { bg: "#f43f5e", light: "#fff1f2", text: "#be123c" },
  { bg: "#f97316", light: "#fff7ed", text: "#c2410c" },
  { bg: "#eab308", light: "#fefce8", text: "#a16207" },
  { bg: "#22c55e", light: "#f0fdf4", text: "#15803d" },
  { bg: "#06b6d4", light: "#ecfeff", text: "#0e7490" },
];

const getColorForLevel = (levelOrder) => {
  const idx = (levelOrder - 1) % LEVEL_COLORS.length;
  return LEVEL_COLORS[idx];
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const getNodeDisplayName = (node) => {
  if (node.office) {
    return node.office.name + (node.office.head_user ? ` (${node.office.head_user.name})` : '');
  }
  return node.user?.name || "Unknown";
};

const getNodeAvatarChar = (node) => {
  if (node.office) return "🏢";
  return getInitials(node.user?.name);
};

// ─── Tree Node Component (recursive) ───
const TreeNodeCard = ({ node, depth = 0, onSelect, selectedId, collapsed, onToggle }) => {
  const color = getColorForLevel(node.level?.level_order || 1);
  const isSelected = selectedId === node.id;
  const isCollapsed = collapsed[node.id];
  const hasChildren =
    node.children_recursive && node.children_recursive.length > 0;

  return (
    <div className="tree-branch">
      <div className="tree-node-wrapper">
        {/* Connector line */}
        {depth > 0 && <div className="connector-vertical" />}

        <div
          className={`tree-node-card ${isSelected ? "selected" : ""}`}
          style={{
            borderColor: color.bg,
            "--node-color": color.bg,
            "--node-light": color.light,
          }}
          onClick={() => onSelect(node)}
        >
          {/* Avatar */}
          <div
            className="node-avatar"
            style={{ backgroundColor: color.bg }}
          >
            {getNodeAvatarChar(node)}
          </div>

          {/* Info */}
          <div className="node-info">
            <div className="node-name">{getNodeDisplayName(node)}</div>
            <div className="node-role" style={{ color: color.text }}>
              {node.level?.level_name || "—"}
            </div>
          </div>

          {/* Commission Badge */}
          <Badge
            bg="none"
            className="node-pct"
            style={{
              backgroundColor: color.light,
              color: color.text,
              border: `1px solid ${color.bg}30`,
            }}
          >
            {parseFloat(node.level?.commission_percentage || 0).toFixed(0)}%
          </Badge>

          {/* Expand/Collapse toggle */}
          {hasChildren && (
            <button
              className="node-toggle"
              onClick={(e) => {
                e.stopPropagation();
                onToggle(node.id);
              }}
              title={isCollapsed ? "Expand" : "Collapse"}
            >
              {isCollapsed ? (
                <span className="toggle-icon">+{node.children_recursive.length}</span>
              ) : (
                <span className="toggle-icon">−</span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Children */}
      {hasChildren && !isCollapsed && (
        <div className="tree-children">
          {/* Horizontal connector for children */}
          <div className="connector-horizontal" />
          <div className="children-list">
            {node.children_recursive.map((child) => (
              <TreeNodeCard
                key={child.id}
                node={child}
                depth={depth + 1}
                onSelect={onSelect}
                selectedId={selectedId}
                collapsed={collapsed}
                onToggle={onToggle}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Page ───
const MlmTreePage = () => {
  const dispatch = useDispatch();
  const {
    tree,
    levels,
    availableUsers,
    totalNodes,
    selectedNode,
    status,
    error,
  } = useSelector((state) => state.mlmTree);

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [catalogServices, setCatalogServices] = useState([]);

  const catalogProducts = useMemo(() => {
    const service = catalogServices.find((s) => s.id === selectedServiceId);
    return service?.products ?? [];
  }, [catalogServices, selectedServiceId]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setCatalogServices([]);
      return;
    }

    api
      .get(`/api/admin/mlm-catalog/${selectedCompanyId}`)
      .then((res) => setCatalogServices(res.data.services ?? []))
      .catch(() => {
        setCatalogServices([]);
        toast.error("Failed to load product catalog");
      });
  }, [selectedCompanyId]);

  useEffect(() => {
    if (catalogServices.length > 0 && !catalogServices.find((s) => s.id === selectedServiceId)) {
      setSelectedServiceId(catalogServices[0].id);
    }
    const products = catalogServices.find((s) => s.id === selectedServiceId)?.products ?? [];
    if (products.length > 0 && !products.find((p) => p.id === selectedProductId)) {
      setSelectedProductId(products[0].id);
    }
  }, [catalogServices, selectedServiceId, selectedProductId]);

  const [offices, setOffices] = useState([]);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [zoom, setZoom] = useState(1);
  const treeContainerRef = useRef(null);

  const [assignFormData, setAssignFormData] = useState({
    assign_type: "user", // "user" or "office"
    user_id: "",
    office_id: "",
    mlm_tree_level_id: "",
    parent_id: "",
  });

  // Load companies on mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const res = await api.get("/api/companies");
        setCompanies(res.data);
        if (res.data.length > 0) {
          setSelectedCompanyId(res.data[0].id);
        }
      } catch {
        toast.error("Failed to load companies");
      }
    };
    loadCompanies();
  }, []);

  // Load offices when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      api.get(`/api/admin/offices?company_id=${selectedCompanyId}`)
        .then(res => setOffices(res.data))
        .catch(err => toast.error("Failed to load offices"));
    } else {
      setOffices([]);
    }
  }, [selectedCompanyId]);

  // Load tree + available users when company, service, or product changes
  useEffect(() => {
    if (selectedCompanyId) {
      dispatch(fetchTree({
        companyId: selectedCompanyId,
        serviceId: selectedServiceId,
        productId: selectedProductId
      }));
      dispatch(fetchAvailableUsers({
        companyId: selectedCompanyId,
        serviceId: selectedServiceId,
        productId: selectedProductId
      }));
    }
  }, [selectedCompanyId, selectedServiceId, selectedProductId, dispatch]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleCompanyChange = (e) => {
    setSelectedCompanyId(e.target.value);
    setCollapsed({});
    dispatch(setSelectedNode(null));
  };

  const handleServiceChange = (e) => {
    setSelectedServiceId(e.target.value);
    setCollapsed({});
    dispatch(setSelectedNode(null));
  };

  const handleProductChange = (e) => {
    setSelectedProductId(e.target.value);
    setCollapsed({});
    dispatch(setSelectedNode(null));
  };

  const handleNodeSelect = (node) => {
    dispatch(setSelectedNode(node));
  };

  const handleToggle = (nodeId) => {
    setCollapsed((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  // ─── Assign User ───

  const openAssignModal = () => {
    setAssignFormData({
      assign_type: "user",
      user_id: "",
      office_id: "",
      mlm_tree_level_id: "",
      parent_id: "",
    });
    dispatch(fetchAvailableUsers({
      companyId: selectedCompanyId,
      serviceId: selectedServiceId,
      productId: selectedProductId
    }));
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (assignFormData.assign_type === 'user' && !assignFormData.user_id) {
      toast.error("Please select a user!");
      return;
    }
    if (assignFormData.assign_type === 'office' && !assignFormData.office_id) {
      toast.error("Please select an office!");
      return;
    }
    if (!assignFormData.mlm_tree_level_id) {
      toast.error("Please select a level!");
      return;
    }
    try {
      await dispatch(
        assignNode({
          company_id: selectedCompanyId,
          service_id: selectedServiceId,
          product_id: selectedProductId,
          user_id: assignFormData.assign_type === 'user' ? parseInt(assignFormData.user_id) : null,
          office_id: assignFormData.assign_type === 'office' ? parseInt(assignFormData.office_id) : null,
          mlm_tree_level_id: parseInt(assignFormData.mlm_tree_level_id),
          parent_id: assignFormData.parent_id
            ? parseInt(assignFormData.parent_id)
            : null,
        })
      ).unwrap();
      toast.success("User assigned to tree!");
      setShowAssignModal(false);
      // Refresh
      dispatch(fetchTree({
        companyId: selectedCompanyId,
        serviceId: selectedServiceId,
        productId: selectedProductId
      }));
      dispatch(fetchAvailableUsers({
        companyId: selectedCompanyId,
        serviceId: selectedServiceId,
        productId: selectedProductId
      }));
    } catch (err) {
      toast.error(err?.message || "Failed to assign user");
    }
  };

  // ─── Remove Node ───

  const handleDeleteClick = () => {
    if (!selectedNode) return;
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedNode) return;
    try {
      await dispatch(removeNode(selectedNode.id)).unwrap();
      toast.success("Node removed from tree!");
      setShowDeleteModal(false);
      dispatch(fetchTree({
        companyId: selectedCompanyId,
        serviceId: selectedServiceId,
        productId: selectedProductId
      }));
      dispatch(fetchAvailableUsers({
        companyId: selectedCompanyId,
        serviceId: selectedServiceId,
        productId: selectedProductId
      }));
    } catch (err) {
      toast.error(err?.message || "Failed to remove node");
    }
  };

  // ─── Zoom ───

  const zoomIn = () => setZoom((z) => Math.min(z + 0.15, 2));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.15, 0.3));
  const resetZoom = () => setZoom(1);

  // ─── Flatten tree for parent dropdown ───

  const flattenTree = (nodes, result = []) => {
    for (const node of nodes) {
      result.push(node);
      if (node.children_recursive && node.children_recursive.length > 0) {
        flattenTree(node.children_recursive, result);
      }
    }
    return result;
  };

  const allNodes = flattenTree(tree);

  // Filter valid parents: must be at a higher level (lower level_order)
  const getValidParents = () => {
    const selectedLevel = levels.find(
      (l) => l.id === parseInt(assignFormData.mlm_tree_level_id)
    );
    if (!selectedLevel) return allNodes;
    return allNodes.filter(
      (n) => n.level && n.level.level_order < selectedLevel.level_order
    );
  };

  // Expand/collapse all
  const expandAll = () => setCollapsed({});
  const collapseAll = () => {
    const map = {};
    allNodes.forEach((n) => {
      if (n.children_recursive && n.children_recursive.length > 0) {
        map[n.id] = true;
      }
    });
    setCollapsed(map);
  };

  return (
    <div className="mlm-tree-page">
      <div className="container-fluid px-4 mt-4">
        {/* ─── Header ─── */}
        <div className="tree-page-header mb-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h3 className="fw-bold tree-title mb-1">
                <span className="me-2">🌳</span>MLM Commission Tree
              </h3>
              <p className="text-muted mb-0">
                Assign users to positions and visualize the tree hierarchy
              </p>
            </div>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <select
                className="form-select company-select-tree"
                value={selectedCompanyId}
                onChange={handleCompanyChange}
              >
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                className="form-select company-select-tree"
                value={selectedServiceId}
                onChange={handleServiceChange}
              >
                {catalogServices.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <select
                className="form-select company-select-tree"
                value={selectedProductId}
                onChange={handleProductChange}
              >
                {catalogProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <Badge bg="primary" className="nodes-badge">
                {totalNodes} node{totalNodes !== 1 ? "s" : ""}
              </Badge>
            </div>
          </div>
        </div>

        <div className="row g-3">
          {/* ─── Left Panel: Tree Visualizer ─── */}
          <div className="col-lg-8 col-xl-9">
            <div className="tree-visualizer-card">
              {/* Toolbar */}
              <div className="tree-toolbar">
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={openAssignModal}
                    disabled={!selectedCompanyId || !selectedServiceId || !selectedProductId || levels.length === 0}
                  >
                    <i className="uil-plus me-1"></i>Assign User
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={expandAll}
                  >
                    Expand All
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={collapseAll}
                  >
                    Collapse All
                  </button>
                </div>
                <div className="zoom-controls">
                  <button className="btn btn-sm btn-outline-secondary" onClick={zoomOut} title="Zoom out">
                    −
                  </button>
                  <span className="zoom-label">{Math.round(zoom * 100)}%</span>
                  <button className="btn btn-sm btn-outline-secondary" onClick={zoomIn} title="Zoom in">
                    +
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={resetZoom} title="Reset zoom">
                    ⟳
                  </button>
                </div>
              </div>

              {/* Tree Canvas */}
              <div className="tree-canvas" ref={treeContainerRef}>
                <div
                  className="tree-root"
                  style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}
                >
                  {status === "loading" && tree.length === 0 ? (
                    <div className="tree-loading">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted">Loading tree...</p>
                    </div>
                  ) : tree.length > 0 ? (
                    <div className="tree-roots-container">
                      {tree.map((root) => (
                        <TreeNodeCard
                          key={root.id}
                          node={root}
                          depth={0}
                          onSelect={handleNodeSelect}
                          selectedId={selectedNode?.id}
                          collapsed={collapsed}
                          onToggle={handleToggle}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="tree-empty">
                      <div className="empty-tree-icon">🌱</div>
                      <h5>No tree nodes yet</h5>
                      <p className="text-muted">
                        {levels.length === 0
                          ? "Configure tree levels first, then assign users."
                          : "Start by assigning users to build the tree."}
                      </p>
                      {levels.length > 0 && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={openAssignModal}
                        >
                          <i className="uil-plus me-1"></i>Assign First User
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Level Legend */}
              {levels.length > 0 && (
                <div className="tree-legend">
                  {levels.map((level) => {
                    const color = getColorForLevel(level.level_order);
                    return (
                      <div key={level.id} className="legend-item">
                        <span
                          className="legend-dot"
                          style={{ backgroundColor: color.bg }}
                        />
                        <span className="legend-label">{level.level_name}</span>
                        <span className="legend-pct" style={{ color: color.text }}>
                          {parseFloat(level.commission_percentage).toFixed(0)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ─── Right Panel: Node Details ─── */}
          <div className="col-lg-4 col-xl-3">
            <div className="detail-panel">
              {selectedNode ? (
                <>
                  <div className="detail-header">
                    <h6 className="fw-bold mb-0">Node Details</h6>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => dispatch(setSelectedNode(null))}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="detail-body">
                    {/* User Info */}
                    <div className="detail-user-card">
                      <div
                        className="detail-avatar"
                        style={{
                          backgroundColor: getColorForLevel(
                            selectedNode.level?.level_order || 1
                          ).bg,
                        }}
                      >
                        {getNodeAvatarChar(selectedNode)}
                      </div>
                      <h5 className="detail-user-name">
                        {getNodeDisplayName(selectedNode)}
                      </h5>
                      <p className="detail-user-email text-muted small mb-0">
                        {selectedNode.office ? selectedNode.office.type.replace('_', ' ').toUpperCase() : selectedNode.user?.email}
                      </p>
                    </div>

                    {/* Node Info */}
                    <div className="detail-info-grid">
                      <div className="detail-info-item">
                        <span className="detail-label">Level</span>
                        <Badge
                          bg="none"
                          style={{
                            backgroundColor: getColorForLevel(
                              selectedNode.level?.level_order || 1
                            ).light,
                            color: getColorForLevel(
                              selectedNode.level?.level_order || 1
                            ).text,
                            border: `1px solid ${
                              getColorForLevel(
                                selectedNode.level?.level_order || 1
                              ).bg
                            }30`,
                          }}
                        >
                          {selectedNode.level?.level_name}
                        </Badge>
                      </div>
                      <div className="detail-info-item">
                        <span className="detail-label">Commission</span>
                        <span className="detail-value fw-bold">
                          {parseFloat(
                            selectedNode.level?.commission_percentage || 0
                          ).toFixed(1)}
                          %
                        </span>
                      </div>
                      <div className="detail-info-item">
                        <span className="detail-label">Level Order</span>
                        <span className="detail-value">
                          #{selectedNode.level?.level_order}
                        </span>
                      </div>
                      <div className="detail-info-item">
                        <span className="detail-label">Children</span>
                        <span className="detail-value">
                          {selectedNode.children_recursive?.length || 0}
                        </span>
                      </div>
                      <div className="detail-info-item">
                        <span className="detail-label">Status</span>
                        <Badge bg={selectedNode.is_active ? "success" : "secondary"}>
                          {selectedNode.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </div>

                    {/* Parent Info */}
                    {selectedNode.parent_id && (
                      <div className="detail-parent-section">
                        <span className="detail-label">Reports to</span>
                        <div className="parent-chip">
                          {/* We don't have parent data in the recursive tree render
                              but we can infer from the tree structure */}
                          <span className="text-muted small">
                            Parent Node #{selectedNode.parent_id}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="detail-actions">
                    <button
                      className="btn btn-outline-danger btn-sm w-100"
                      onClick={handleDeleteClick}
                    >
                      <i className="uil-trash-alt me-1"></i>Remove from Tree
                    </button>
                  </div>
                </>
              ) : (
                <div className="detail-empty">
                  <div className="detail-empty-icon">👆</div>
                  <h6 className="text-muted">Select a node</h6>
                  <p className="text-muted small">
                    Click on any node in the tree to see details and actions
                  </p>
                </div>
              )}
            </div>

            {/* ─── Quick Stats ─── */}
            <div className="stats-panel mt-3">
              <h6 className="fw-bold mb-3">
                <span className="me-1">📊</span>Tree Stats
              </h6>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-num">{totalNodes}</span>
                  <span className="stat-label">Total Nodes</span>
                </div>
                <div className="stat-item">
                  <span className="stat-num">{levels.length}</span>
                  <span className="stat-label">Levels</span>
                </div>
                <div className="stat-item">
                  <span className="stat-num">{tree.length}</span>
                  <span className="stat-label">Root Nodes</span>
                </div>
                <div className="stat-item">
                  <span className="stat-num">{availableUsers.length}</span>
                  <span className="stat-label">Unassigned</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Assign Modal ─── */}
        <Modal
          show={showAssignModal}
          onHide={() => setShowAssignModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Assign User to Tree</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleAssignSubmit}>
              <div className="row g-3">
                <div className="col-12">
                  <div className="btn-group w-100" role="group">
                    <input type="radio" className="btn-check" name="btnradio" id="btnradio1" autoComplete="off" 
                      checked={assignFormData.assign_type === 'user'} 
                      onChange={() => setAssignFormData({...assignFormData, assign_type: 'user', office_id: ''})} />
                    <label className="btn btn-outline-primary" htmlFor="btnradio1">Assign User</label>

                    <input type="radio" className="btn-check" name="btnradio" id="btnradio2" autoComplete="off" 
                      checked={assignFormData.assign_type === 'office'} 
                      onChange={() => setAssignFormData({...assignFormData, assign_type: 'office', user_id: ''})} />
                    <label className="btn btn-outline-primary" htmlFor="btnradio2">Assign Office</label>
                  </div>
                </div>

                <div className="col-md-6">
                  {assignFormData.assign_type === 'user' ? (
                    <>
                      <label className="form-label">User *</label>
                      <select
                        className="form-select"
                        value={assignFormData.user_id}
                        onChange={(e) =>
                          setAssignFormData({
                            ...assignFormData,
                            user_id: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Select User</option>
                        {availableUsers.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name} ({u.email})
                          </option>
                        ))}
                      </select>
                      {availableUsers.length === 0 && (
                        <small className="text-warning">
                          No available users. All company members are already in the tree.
                        </small>
                      )}
                    </>
                  ) : (
                    <>
                      <label className="form-label">Office *</label>
                      <select
                        className="form-select"
                        value={assignFormData.office_id}
                        onChange={(e) =>
                          setAssignFormData({
                            ...assignFormData,
                            office_id: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Select Office</option>
                        {offices.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name} ({o.type.replace('_', ' ')})
                          </option>
                        ))}
                      </select>
                      {offices.length === 0 && (
                        <small className="text-warning">
                          No offices found for this company.
                        </small>
                      )}
                    </>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Level *</label>
                  <select
                    className="form-select"
                    value={assignFormData.mlm_tree_level_id}
                    onChange={(e) =>
                      setAssignFormData({
                        ...assignFormData,
                        mlm_tree_level_id: e.target.value,
                        parent_id: "", // Reset parent when level changes
                      })
                    }
                    required
                  >
                    <option value="">Select Level</option>
                    {levels.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.level_name} (Order #{l.level_order} —{" "}
                        {parseFloat(l.commission_percentage).toFixed(0)}%)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">
                    Parent (Reports To){" "}
                    {assignFormData.mlm_tree_level_id &&
                      levels.find(
                        (l) =>
                          l.id === parseInt(assignFormData.mlm_tree_level_id)
                      )?.level_order === 1 && (
                        <small className="text-muted">
                          — Top level, no parent needed
                        </small>
                      )}
                  </label>
                  <select
                    className="form-select"
                    value={assignFormData.parent_id}
                    onChange={(e) =>
                      setAssignFormData({
                        ...assignFormData,
                        parent_id: e.target.value,
                      })
                    }
                  >
                    <option value="">No Parent (Root Node)</option>
                    {getValidParents().map((n) => (
                      <option key={n.id} value={n.id}>
                        {getNodeDisplayName(n)} — {n.level?.level_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowAssignModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAssignSubmit}>
              Assign to Tree
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ─── Delete Modal ─── */}
        {showDeleteModal && selectedNode && (
          <DeleteConfirmationModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title="Remove from Tree"
            message={`Remove "${selectedNode.office ? selectedNode.office.name : selectedNode.user?.name}" from the tree? ${
              selectedNode.children_recursive?.length > 0
                ? `Their ${selectedNode.children_recursive.length} child node(s) will be moved up to the parent.`
                : ""
            }`}
          />
        )}
      </div>
    </div>
  );
};

export default MlmTreePage;
